const isObjectLike = (value) => typeof value === 'object' && value !== null

const toTrimmedString = (value) => (typeof value === 'string' ? value.trim() : '')

const parseJsonString = (value) => {
  const trimmed = toTrimmedString(value)
  if (!trimmed) return null
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null
  try {
    return JSON.parse(trimmed)
  } catch {
    return null
  }
}

const looksLikeException = (value) => /exception$/i.test(toTrimmedString(value))

const pushIfPresent = (queue, value) => {
  if (value === undefined || value === null) return
  queue.push(value)
}

const isWeakMessage = (value) => {
  const msg = toTrimmedString(value).toLowerCase()
  if (!msg) return true
  return (
    msg === 'error' ||
    msg === 'request failed' ||
    msg === '[object object]' ||
    msg === 'an error occurred' ||
    msg === 'unknown error' ||
    msg === 'network error'
  )
}

const collectErrorDetails = (source) => {
  const queue = [source]
  const visited = new Set()
  let message = ''
  let code = ''

  while (queue.length > 0) {
    const current = queue.shift()
    if (current === undefined || current === null) continue

    if (typeof current === 'string') {
      const parsed = parseJsonString(current)
      if (parsed) {
        queue.push(parsed)
        continue
      }
      if (!message) message = current.trim()
      continue
    }

    if (current instanceof Error) {
      if (!code) code = toTrimmedString(current.name)
      pushIfPresent(queue, current.message)
      pushIfPresent(queue, current.body)
      pushIfPresent(queue, current.response)
      pushIfPresent(queue, current.data)
      pushIfPresent(queue, current.payload)
      pushIfPresent(queue, current.cause)
      continue
    }

    if (!isObjectLike(current)) continue
    if (visited.has(current)) continue
    visited.add(current)

    const typeCandidate = toTrimmedString(
      current.__type || current.code || current.name || current.errorType || current.type
    )
    if (!code && typeCandidate) code = typeCandidate

    const directMessageCandidates = [
      current.errorMessage,
      current.error_description,
      current.error,
      current.message,
      current.detail,
      current.title,
      current.reason
    ]

    for (const candidate of directMessageCandidates) {
      if (!candidate) continue
      if (typeof candidate === 'string') {
        const parsed = parseJsonString(candidate)
        if (parsed) {
          queue.push(parsed)
          continue
        }
        if (!message || isWeakMessage(message)) {
          message = candidate.trim()
          break
        }
        continue
      }
      pushIfPresent(queue, candidate)
    }

    if (Array.isArray(current.errors)) {
      current.errors.forEach((item) => pushIfPresent(queue, item))
    }

    const nestedCandidates = [
      current.body,
      current.data,
      current.payload,
      current.response,
      current.cause,
      current.error
    ]
    nestedCandidates.forEach((item) => pushIfPresent(queue, item))
  }

  return {
    message: toTrimmedString(message),
    code: toTrimmedString(code)
  }
}

export const extractAuthErrorDetails = (source, fallbackMessage = 'Request failed.') => {
  const fallback = toTrimmedString(fallbackMessage) || 'Request failed.'
  const details = collectErrorDetails(source)
  return {
    message: details.message || fallback,
    code: details.code || ''
  }
}

export const extractAuthErrorMessage = (source, fallbackMessage = 'Request failed.') => (
  extractAuthErrorDetails(source, fallbackMessage).message
)

export const isAuthErrorPayload = (payload) => {
  if (typeof payload === 'string') {
    const parsed = parseJsonString(payload)
    if (parsed) return isAuthErrorPayload(parsed)
    return looksLikeException(payload)
  }

  if (!isObjectLike(payload)) return false

  if (payload.ok === false || payload.success === false) return true
  if (toTrimmedString(payload.errorMessage)) return true
  if (toTrimmedString(payload.__type)) return true
  if (toTrimmedString(payload.code) && looksLikeException(payload.code)) return true
  if (toTrimmedString(payload.name) && looksLikeException(payload.name)) return true

  const status = Number(payload.status ?? payload.statusCode)
  if (!Number.isNaN(status) && status >= 400) return true

  if (typeof payload.error === 'string' && toTrimmedString(payload.error)) return true
  if (isObjectLike(payload.error)) return true
  if (Array.isArray(payload.errors) && payload.errors.length > 0) return true

  return false
}

export const assertNoAuthErrorPayload = (payload, fallbackMessage = 'Request failed.') => {
  if (typeof payload === 'string') {
    const parsed = parseJsonString(payload)
    if (parsed) return assertNoAuthErrorPayload(parsed, fallbackMessage)
  }

  if (!isAuthErrorPayload(payload)) return payload

  const { message, code } = extractAuthErrorDetails(payload, fallbackMessage)
  const error = new Error(message)
  if (code) error.name = code
  error.body = payload
  throw error
}
